define((require) => {
    "use strict";

    const Backbone        = require("backbone");
    const Injector        = require("shared/util/Injector");
    const Configuration   = require("shared/services/Configuration");
    const EventDispatcher = require("shared/util/EventDispatcher"); // included here because of circular dependencies other way around
    const {Logger}        = require("shared/util/Logger");

    const log = new Logger("Events");

    /*
     * Patches Backbone.Events.trigger to catch any unhandled errors, to allow the context (e.g. model) to continue
     * with with events propagation to avoid it leaving the context (model) in some state that will prevent further
     * event propagation (forever, until reload!). Instead, any unhandled error is raised with a timeout (of zero),
     * so the error(s) is (are) still raised, but in a manner that will allow "normal execution" afterwards. But,
     * since unhandled errors may still occur, the context may still be bogus, but at least we can prevent the
     * editor from not doing any more work, like not trying to save a model.
     *
     * For further discussion, see: https://github.com/jashkenas/backbone/issues/1527
     */

    const eventSplitter = /\s+/;
    let errorQueue = [];
    let firstError = null;
    let errorSequence = 1;
    let errorCount = 0;
    let timer = null;

    // Implement fancy features of the Events API such as multiple event
    // names `"change blur"` and jQuery-style event maps `{change: action}`
    // in terms of the existing API.
    function eventsApi(obj, action, name, rest) { // Copied and fixed for warnings from Backbone.Event
        if (!name) {
            return true;

        // Handle event maps.
        } else if (typeof name === "object") {
            for (const key in name) {
                obj[action](...[key, name[key]].concat(rest));
            }
            return false;

        // Handle space separated event names.
        } else if (eventSplitter.test(name)) {
            const names = name.split(eventSplitter);
            const l = names.length;
            let i;

            for (i = 0; i < l; i++) {
                obj[action](...[names[i]].concat(rest));
            }
            return false;
        }

        return true;
    }

    /**
     * @private
     * Returns the configured error logger
     *
     * @returns {ErrorLogger} The configured error logger, or null if not available.
     */
    function errorLogger() {
        // This can potentially go sour if the error logger has not be registered yet, i.e. we have a unhandled
        // error as part of the Backbone app initialisation...
        if (!errorLogger.logger) {
            if (Injector.has("errorLogger")) {
                errorLogger.logger = Injector.get("errorLogger");
            }
        }
        return errorLogger.logger;
    }

    /**
     * @private
     * Reports the supplied error by logging it to the console and reporting it via the <tt>ErrorLogger</tt> used,
     * if any. <p>
     *
     * The first reported error is ignored by this, since it will be re-raised as unhandled later on, thus reported
     * there.
     *
     * @param {*} entry The (queued) entry <tt>{scope:*, errors:[]{error:Error|String, order:Number}}</tt>,
     *                  to report; never null.
     */
    function reportErrors(entry) {
        let i;
        let err;
        const errors = entry.errors;
        const logger = errorLogger();

        for (i = 0; i < errors.length; i++) {
            err = errors[i];
            // Already reported, since that is the first one and thus raised again as unhandled...
            if (err.order >= 1) { // if not, error.error === firstError!
                // Tag message with sequence for easier tracking in the frontend service error log, will also
                // be set on the first error re-thrown. If an error already, this can be a read-only property,
                // so we have to guard it with a try-catch...
                let resolved;
                try {
                    resolved = (err.error.message || "unknown error") + (errorCount > 1 ? ` (#${err.sequence}.${err.order})` : "");
                    err.error.message = resolved;
                } catch (e) { // e.g. TypeError, SecurityError
                    // Fall-through...
                }

                // Akin to unhandled exceptions being logged as errors by default by the browser...
                log.error(`Uncaught ${err.error}, ${err.error.stack || err.error}`);
                // Mimics behaviour for unhandled errors in ErrorLogger, but with a bit more info...
                if (logger) {
                    logger.log({
                        unhandled: resolved || err.error.message, // since the try-catch can fail above!
                        trace:     err.error.stack && err.error.stack.toString().split(/\s*\n+\s*/g),
                        sequence:  err.sequence,
                        order:     err.order
                    });
                }
            }
        }
    }

    /**
     * @private
     * Finds or creates a new queue for the supplied scope.
     *
     * @param   {*} scope The scope to find or create the error queue for; never null.
     *
     * @returns {{scope:*, errors:Error[]}} The (created) queue for the supplied scope; never null.
     */
    function queue(scope) {
        // To avoid re-queueing errors on "error:unhandled" events...
        if (!errorQueue) {
            return null;
        }
        let i;
        let entry;

        // Does an existing queue for the supplied scope exist?
        for (i = 0; i < errorQueue.length; i++) {
            entry = errorQueue[i];
            if (entry.scope === scope) {
                return entry;
            }
        }
        // Create...
        entry = {scope, errors: []};
        errorQueue.push(entry);
        return entry;
    }

    /**
     * @private
     * Notifies all queued (scope, error) pairs about the unhandled errors via the "error:unhandled" event,
     * without re-queuing errors again, and then raises the first queued unhandled error. <p>
     *
     * The existing error queue is cleared by this.
     */
    function raiseError() {
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
        // Temporarily setting the error queue to null ensures that unhandled errors raised by "error:unhandled" will
        // not cause an infinite loop...
        const errors = errorQueue;
        const first = firstError;
        let entry;
        let i;

        errorQueue = null;
        firstError = null;

        // Notify each scope of the error. Even though we have disabled the error queueing, we will still catch
        // unhandled errors during this notification, but merely log the errors. We cannot bootstrap the reporting
        // of frontend errors to listen for "error:unhandled", since if the frontend error service raises an error,
        // we cannot report it via it self (infinite loop). We therefore have to call the frontend error service
        // explicitly here...
        for (i = 0; i < errors.length; i++) {
            entry = errors[i];
            entry.scope.trigger("error:unhandled", entry);
            // Report errors to the ErrorLogger...
            reportErrors(entry);
        }

        // May have increased during "error:unhandled"! We tag the message with sequence information for better
        // tracing in the frontend...
        if (errorCount > 1) {
            first.message += ` (#${errorSequence}.1)`;
        }

        // Ready to queue again!
        errorQueue = [];
        errorCount = 0;
        errorSequence++;

        // Browser will log this since unhandled...
        throw first;
    }

    /**
     * Queues the unhandled error for the supplied scope. <p>
     *
     * If this is the very first error queued since the last raised error (if any), the supplied error will be
     * raised again via a timeout, to allow all pending events to be triggered.
     *
     * @param {*}            scope The scope associated with the error; never null.
     * @param {Error|String} error The unhandled error; never null.
     */
    function queueError(scope, error) {
        const entry = queue(scope);
        const err = {error, sequence: errorSequence, order: ++errorCount};

        if (entry) {
            entry.errors.push(err);
            if (!timer) {
                firstError = error;
                timer = setTimeout(raiseError, 0);
            }
        } else {
            reportErrors({scope, errors: [err]});
        }
    }

    /**
     * Triggers the supplied events with the specified arguments. <p>
     *
     * Any error raised from a called event handler will be caught and queued for later reporting, allowing all
     * event handlers to be notified. This fundamentally changes Backbone behaviour, since normally uncaught
     * exceptions could/would break it, i.e. events not triggered henceforth (i.e. saving suddenly stops).
     *
     * @param {String[]} events Array of the events to fire, in order; never null, never empty.
     * @param {[]}       args   Arguments for each fired event, in order; never null, but can be empty.
     */
    function triggerEvents(events, args) { // Copied, patched (try-catch), and fixed for warnings from Backbone.Event
        let ev;
        let i = -1;
        const l = events.length;
        const a1 = args[0];
        const a2 = args[1];
        const a3 = args[2];

        switch (args.length) {
            case 0:  while (++i < l) try { (ev = events[i]).callback.call(ev.ctx); }             catch (e) { queueError(ev.ctx, e); } return;
            case 1:  while (++i < l) try { (ev = events[i]).callback.call(ev.ctx, a1); }         catch (e) { queueError(ev.ctx, e); } return;
            case 2:  while (++i < l) try { (ev = events[i]).callback.call(ev.ctx, a1, a2); }     catch (e) { queueError(ev.ctx, e); } return;
            case 3:  while (++i < l) try { (ev = events[i]).callback.call(ev.ctx, a1, a2, a3); } catch (e) { queueError(ev.ctx, e); } return;
            default: while (++i < l) try { (ev = events[i]).callback.apply(ev.ctx, args); }      catch (e) { queueError(ev.ctx, e); }
        }
    }

    /**
     * Triggers the specified event name for the current scope, ensuring that errors will not cause
     * the execution to stop. Errors are recorded, and once all handlers have been notified, the
     * first error will be re-raised. <p>
     *
     * Arguments 1-n will be supplied as arguments for the triggered event(s).
     *
     * @param {String|*} name Event name(s) to trigger, separated by space, or an object like {name: action, ..}.
     */
    const zmagsTrigger = function(name, ...args) { // Copied and fixed for warnings from Backbone.Event
        if (!this._events) {
            return this;
        }
        if (!eventsApi(this, "trigger", name, args)) {
            return this;
        }
        const events = this._events[name];
        const allEvents = this._events.all;

        if (events) {
            triggerEvents(events, args);
        }
        if (allEvents) {
            const triggerArgs = [name].concat(args);
            triggerEvents(allEvents, triggerArgs);
        }
        return this;
    };

    // Allow safe mode to be turned off, i.e. use original Backbone functionality. This is basically a test feature
    // to allows us to ensure that the system will run - assuming no unhandled errors - the same with or without
    // this event patch!...
    const safeMode = function() {
        return Configuration.val({
            name: "zmagsSafeMode", configName: "safeMode", type: "number", defaultValue: 1
        });
    };

    /**
     * Patches the supplied named target recursively through its prototype chain with the patched trigger function. <p>
     *
     * Important: the scope for the trigger function is always correct, even when a given object is patched and not
     * its prototype, so we do not have to bind "zmagsTrigger" to the object it self.
     *
     * @param {*}      target The target to patch, i.e. replace its "trigger" function with the patched version.
     * @param {String} name   The user-friendly name of target, for logging purposes only; never null.
     */
    const patchTarget = (target, name) => {
        const type = typeof target;
        // Object for prototypes and plain on {}, function for types...
        if (target && (type === "object" || type === "function")) {
            if (typeof target.trigger === "function" && target.trigger !== zmagsTrigger) {
                target.trigger = zmagsTrigger; // will have correct scope!
                log.verbose(`Patched: ${name}.trigger`, target);
            }
            // Check prototype recursively...
            const proto = target.prototype;
            if (proto) {
                patchTarget(proto, `${name}:proto`); // do not call "patch" here, since we would have to maintain a graph to avoid circular dependencies!
            }
        }
    };

    /**
     * Patches the supplied named target and any relevant property, all recursively through their prototype chains
     * with the patched trigger function. That is, if <tt>target</tt> declares or inherits a "trigger" function via
     * is prototype, it will be patched, and the same is true for any other declared property (expect "$", which is
     * ignored). Recursion does not apply to properties of properties, only first level ones. <p>
     *
     * Note: if the configuration property "zmagsSafeMode"/"safeMode" is set to 0, no patching is made! This is a
     * debug feature only, to ensure that the patch will not affect normal execution (i.e. when no errors).
     *
     * @param {*}      target The target to patch, i.e. replace its "trigger" function with the patched version.
     * @param {String} name   The user-friendly name of target, for logging purposes only; never null.
     *
     * @return {Function} This patch function for chaining; never null.
     */
    const patch = (target, name) => {
        if (safeMode()) {
            name = name || target.name || "?";
            // Try patching "target" itself, e.g. "target.trigger"...
            patchTarget(target, name);
            // Try patching each property recursively then...
            let key;
            for (key in target) {
                // Different kind of trigger... :)
                if (key !== "$") {
                    patchTarget(target[key], `${name}.${key}`);
                }
            }
        } else {
            log.verbose(`Non safe-mode, will not patch: ${name}`, target);
        }
        return patch;
    };

    // For objects who has yet to extend Backbone.Events *and* for objects that we know have assigned Backend.Events
    // to their prototype...
    return patch(Backbone, "Backbone")(EventDispatcher, "EventDispatcher"); // included here because of circular dependencies!
});



// WEBPACK FOOTER //
// ./src/shared/zbone/Events.js